import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { DuplicatePassenger, MatchFields } from './management/merge/types';
import type { Passenger } from './Passenger';
import type { PassengerImportResultRead } from 'src/common/external/bambi-api/bambiApi';

export type FilterAllowSMSMessage = 'all' | 'yes' | 'no';

export interface IPassengerState {
  passengers: Passenger[];
  passengerSearchTerm: string;
  passengerMergeModalOpen: boolean;
  // merge modal
  selectedMatchFields: MatchFields[];
  selectedPassengers: DuplicatePassenger[];
  searchPassengerFirst: string;
  searchPassengerLast: string;
  parentPassenger: DuplicatePassenger | null;
  showDeletedPassengers: boolean;
  // import modal
  showImportModal: boolean;
  importing: boolean;
  importTaskId: string | null;
  importTaskResult: PassengerImportResultRead | null;

  // Filters
  filterAllowSMSMessage: FilterAllowSMSMessage;
  selectedTags: string[];
}

export const initialState: IPassengerState = {
  passengers: [],
  passengerSearchTerm: '',
  passengerMergeModalOpen: false,
  selectedMatchFields: [],
  selectedPassengers: [],
  searchPassengerFirst: '',
  searchPassengerLast: '',
  parentPassenger: null,
  showDeletedPassengers: false,
  filterAllowSMSMessage: 'all',
  selectedTags: [],
  showImportModal: false,
  importing: false,
  importTaskId: null,
  importTaskResult: null,
};

export const passengerSlice = createSlice({
  name: 'passenger',
  initialState,
  reducers: {
    setPassengers: (state, action: { payload: Passenger[]; type: string }) => {
      state.passengers = action.payload;
    },
    setPassengerSearchTerm: (state, action: PayloadAction<string>) => {
      state.passengerSearchTerm = action.payload;
    },
    setPassengerMergeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.passengerMergeModalOpen = action.payload;
    },
    setSearchPassengerFirst: (state, action: PayloadAction<string>) => {
      state.searchPassengerFirst = action.payload;
    },
    setSearchPassengerLast: (state, action: PayloadAction<string>) => {
      state.searchPassengerLast = action.payload;
    },
    setSelectedMatchFields: (state, action: PayloadAction<MatchFields[]>) => {
      state.selectedMatchFields = action.payload;
    },
    setDuplicatePassengers: (
      state,
      action: PayloadAction<DuplicatePassenger[]>
    ) => {
      state.selectedPassengers = action.payload;
    },
    setParentPassenger: (state, action: PayloadAction<DuplicatePassenger>) => {
      state.parentPassenger = action.payload;
    },
    setShowDeletedPassengers: (state, action: PayloadAction<boolean>) => {
      state.showDeletedPassengers = action.payload;
    },
    resetMergeWizard: (state) => {
      state.passengerMergeModalOpen = false;
      state.parentPassenger = null;
      state.searchPassengerFirst = '';
      state.searchPassengerLast = '';
      state.selectedPassengers = [];
      state.selectedMatchFields = [];
    },
    setFilterAllowSMSMessage: (
      state,
      action: PayloadAction<FilterAllowSMSMessage>
    ) => {
      state.filterAllowSMSMessage = action.payload;
    },
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    clearFilters: (state) => {
      state.filterAllowSMSMessage = 'all';
      state.selectedTags = [];
    },
    setShowImportModal: (state, action: PayloadAction<boolean>) => {
      state.showImportModal = action.payload;
    },
    setImporting: (state, action: PayloadAction<boolean>) => {
      state.importing = action.payload;
    },
    setImportTaskId: (state, action: PayloadAction<string | null>) => {
      state.importTaskId = action.payload;
    },
    setImportTaskResult: (
      state,
      action: PayloadAction<PassengerImportResultRead | null>
    ) => {
      state.importTaskResult = action.payload;
    },
  },
  selectors: {
    hasFilters: (state) => {
      return (
        state.filterAllowSMSMessage !== 'all' || state.selectedTags.length > 0
      );
    },
  },
});

export const {
  setPassengers,
  setPassengerSearchTerm,
  // Passenger merge modal
  setPassengerMergeModalOpen,
  setSearchPassengerFirst,
  setSearchPassengerLast,
  setSelectedMatchFields,
  setDuplicatePassengers,
  setParentPassenger,
  setShowDeletedPassengers,
  resetMergeWizard,
  setFilterAllowSMSMessage,
  setSelectedTags,
  clearFilters,
  setShowImportModal,
  setImporting,
  setImportTaskId,
  setImportTaskResult,
} = passengerSlice.actions;

export const passengerSelectors = passengerSlice.selectors;
